/**
 * Signal trace: the live map of where everything on one physical port comes
 * from (docs/signal-flow-tracing-plan.md).
 *
 * One trace gives one tree for the video and one tree per audio channel.
 * Each tree starts at the port and follows its feeds back: links (input
 * ports to the output on the other end), the routes inside devices and Dante
 * subscriptions, until every branch reaches a head (a camera, colour bars, a
 * vMix input, a multiviewer), a mix ("Main Mix", "ATEM program audio") or a
 * dead end. `signal-trace-lanes.ts` folds the channel trees into pairs.
 *
 * Routes are not stored: they come from the device stores (live from the
 * bridge, else the config's stored `device_states`), the Dante network state
 * and the config's vMix output setting. Each device answers one question
 * through its adapter: for this output (a port or a Dante channel) and this
 * video or audio channel, what feeds it?
 *
 * Pure, runs in the browser on every change of the stores.
 */
import { captureCardPortAddress, getDeviceTemplate, } from "./device-templates.js";
import { danteChannelKey, danteChannelName } from "./dante.js";
import { atemSourceName, } from "./signal-trace.js";
import { VMIX_OUTPUT_LABELS } from "./vmix-outputs.js";
import { portLabel, } from "./wiring.js";
// ─── Channels ─────────────────────────────────────────────────
/** Audio channels a port carries, from its signal. */
export const SIGNAL_AUDIO_CHANNELS = {
    sdi: 16,
    fiber: 16,
    hdmi: 8,
    madi: 64,
    aes: 2,
    analog_audio: 1,
};
const VIDEO_SIGNALS = new Set(["sdi", "fiber", "hdmi"]);
export function portAudioChannels(port) {
    return SIGNAL_AUDIO_CHANNELS[port.signal] ?? 0;
}
export function portCarriesVideo(port) {
    return VIDEO_SIGNALS.has(port.signal);
}
/** SDI channels a device originates when nothing says otherwise. */
export const DEFAULT_EMBEDDED_AUDIO = "1-2";
/**
 * "1-4, 7-8" → [1, 2, 3, 4, 7, 8]. Invalid parts are skipped; an empty
 * string is no channels.
 */
export function parseChannelList(text) {
    const channels = new Set();
    for (const part of text.split(",")) {
        const match = part.trim().match(/^(\d+)(?:\s*[-–]\s*(\d+))?$/);
        if (!match)
            continue;
        const from = Number(match[1]);
        const to = match[2] ? Number(match[2]) : from;
        for (let channel = from; channel <= Math.min(to, 256); channel++) {
            if (channel >= 1)
                channels.add(channel);
        }
    }
    return [...channels].sort((a, b) => a - b);
}
/** The SDI channels a device originates: its own setting, its template's, 1+2. */
export function deviceEmbeddedAudio(device) {
    const text = device.embeddedAudio ??
        getDeviceTemplate(device.templateID)?.embeddedAudio ??
        DEFAULT_EMBEDDED_AUDIO;
    return parseChannelList(text);
}
/** Drivers whose Dante channels are not on their own ports. */
const OWN_DANTE_ADAPTER = new Set(["dlive", "vmix", "behringer_wing"]);
/**
 * The Dante channels of a Dante device's physical audio ports: its output
 * ports take its rx channels in port order, its input ports its tx channels,
 * each as many as its signal carries (AES out 1 = rx 1+2, AES out 2 = rx
 * 3+4), from the template's offset on. Bidirectional ports take none.
 */
export function dantePortChannels(device, ports) {
    const offset = getDeviceTemplate(device.templateID)?.danteChannelOffset ?? 0;
    const result = new Map();
    const own = ports
        .filter((port) => port.deviceID === device.id && portAudioChannels(port))
        .sort((a, b) => (a.group ?? "").localeCompare(b.group ?? "") || a.number - b.number);
    for (const [direction, portDirection] of [
        ["rx", "output"],
        ["tx", "input"],
    ]) {
        let next = offset + 1;
        for (const port of own) {
            if (port.direction !== portDirection)
                continue;
            const count = portAudioChannels(port);
            result.set(port.id, { direction, first: next, count });
            next += count;
        }
    }
    return result;
}
const end = (status, reason, extra = {}) => ({ kind: "end", status, reason, ...extra });
const feeds = (...list) => ({
    kind: "feeds",
    feeds: list,
});
/** Channel side by its parity: 1, 3, ... are left. */
const sideOf = (channel) => (channel % 2 ? "L" : "R");
class Tracer {
    wiring;
    inputs;
    portByID;
    deviceByID;
    portsByDevice = new Map();
    deviceByDanteName = new Map();
    danteChannels = new Map();
    constructor(wiring, inputs) {
        this.wiring = wiring;
        this.inputs = inputs;
        this.portByID = new Map(wiring.ports.map((port) => [port.id, port]));
        this.deviceByID = new Map(wiring.devices.map((d) => [d.id, d]));
        for (const port of wiring.ports) {
            const list = this.portsByDevice.get(port.deviceID) ?? [];
            list.push(port);
            this.portsByDevice.set(port.deviceID, list);
        }
        for (const device of wiring.devices) {
            const name = device.danteName?.trim();
            if (name)
                this.deviceByDanteName.set(name, device);
        }
    }
    // ── State ──
    store(deviceID) {
        return this.inputs.routes?.[deviceID] ?? null;
    }
    value(deviceID, path) {
        return this.store(deviceID)?.values?.[path];
    }
    number(deviceID, path) {
        const value = this.value(deviceID, path);
        if (value === undefined || value === "" || typeof value === "boolean") {
            return undefined;
        }
        const number = Number(value);
        return Number.isFinite(number) ? number : undefined;
    }
    text(deviceID, path) {
        const value = this.value(deviceID, path);
        return typeof value === "string" && value.trim() ? value : undefined;
    }
    truthy(deviceID, path) {
        const value = this.value(deviceID, path);
        return value === true || value === "true" || value === "True";
    }
    stateFrom(deviceID) {
        return this.inputs.routeSources?.get(deviceID);
    }
    deviceName(deviceID) {
        return this.deviceByID.get(deviceID)?.name ?? "the device";
    }
    noState(deviceID) {
        return end("unknown", `No state for ${this.deviceName(deviceID)}`);
    }
    // ── Wiring ──
    /** Whether the signal arrives at the port over its cable. */
    arriving(port, uncabled) {
        if (port.direction === "input")
            return true;
        if (port.direction === "output")
            return false;
        const link = this.wiring.linkByPort.get(port.id);
        return link ? link.toPortID === port.id : uncabled;
    }
    controlled(deviceID, match) {
        return this.portsByDevice
            .get(deviceID)
            ?.find((port) => !!port.control && match(port.control));
    }
    isDanteOnPorts(device) {
        return (!!device.danteName?.trim() &&
            !OWN_DANTE_ADAPTER.has(device.connectionType));
    }
    dantePorts(device) {
        let map = this.danteChannels.get(device.id);
        if (!map) {
            map = dantePortChannels(device, this.portsByDevice.get(device.id) ?? []);
            this.danteChannels.set(device.id, map);
        }
        return map;
    }
    /** The vMix software on the PC a capture card sits in. */
    vmixOf(card) {
        if (!card.parentID)
            return undefined;
        return this.wiring.devices.find((d) => d.parentID === card.parentID && d.connectionType === "vmix");
    }
    /** vMix on the device itself or among its children (the vMix PC). */
    hasVmix(device) {
        return (device.connectionType === "vmix" ||
            this.wiring.devices.some((d) => d.parentID === device.id && d.connectionType === "vmix"));
    }
    portName(port) {
        const device = this.deviceByID.get(port.deviceID);
        return `${device ? `${device.name} ` : ""}${portLabel(port)}`;
    }
    // ── Points ──
    portNode(port, media) {
        return {
            deviceID: port.deviceID,
            portID: port.id,
            ...(media.kind === "audio" ? { channel: media.channel } : {}),
            label: this.portName(port),
            status: "resolved",
            feeds: [],
        };
    }
    cycle(node, label) {
        return {
            ...node,
            status: "cycle",
            reason: `The signal loops back to ${label}`,
            feeds: [],
        };
    }
    /** The signal on any port: arriving over its cable or leaving it. */
    port(port, media, path, uncabled = true) {
        return this.arriving(port, uncabled)
            ? this.input(port, media, path)
            : this.output(port, media, path);
    }
    /** An input port: fed by the output at the other end of its link. */
    input(port, media, path) {
        const node = this.portNode(port, media);
        const key = `in:${port.id}:${media.kind === "audio" ? media.channel : "v"}`;
        if (path.has(key))
            return this.cycle(node, node.label);
        const next = new Set(path).add(key);
        const link = this.wiring.linkByPort.get(port.id);
        if (!link || link.toPortID !== port.id) {
            const suspended = this.wiring.suspendedPorts.get(port.id);
            return suspended
                ? { ...node, status: "suspended", reason: suspended.reason }
                : { ...node, status: "not_cabled", reason: "not cabled" };
        }
        node.link = linkInfo(link);
        const from = this.portByID.get(link.fromPortID);
        if (!from) {
            return {
                ...node,
                status: "unknown",
                reason: "The cable comes from a port that is not in the wiring",
            };
        }
        node.feeds = [{ node: this.output(from, media, next) }];
        return node;
    }
    /** An output port: what feeds it, answered by its device's adapter. */
    output(port, media, path) {
        const node = this.portNode(port, media);
        const key = `out:${port.id}:${media.kind === "audio" ? media.channel : "v"}`;
        if (path.has(key))
            return this.cycle(node, node.label);
        const next = new Set(path).add(key);
        const device = this.deviceByID.get(port.deviceID);
        if (!device) {
            return { ...node, status: "unknown", reason: "Device not in the wiring" };
        }
        const result = this.adapter(device, port, media, next, node);
        return applyResult(node, result);
    }
    // ── Adapters ──
    adapter(device, port, media, path, node) {
        const control = port.control;
        if (control?.kind === "videohub_output") {
            node.stateFrom = this.stateFrom(device.id);
            return this.videohub(device, control.index, media, path);
        }
        if (device.connectionType === "atem" ||
            control?.kind === "atem_aux" ||
            control?.kind === "atem_multiviewer") {
            node.stateFrom = this.stateFrom(device.id);
            return this.atemOutput(device, port, media, path);
        }
        if (getDeviceTemplate(device.templateID)?.captureCard) {
            return this.captureCard(device, port, media, path);
        }
        if (media.kind === "audio") {
            if (this.isDanteOnPorts(device)) {
                const danteResult = this.danteOutputPort(device, port, media, path);
                if (danteResult) {
                    if (portCarriesVideo(port))
                        node.embedding = "embedded";
                    return danteResult;
                }
            }
            if (device.connectionType === "dlive") {
                node.stateFrom = this.stateFrom(device.id);
                return this.dlive(device, `port${port.number}`, media.channel);
            }
            if (device.connectionType === "behringer_wing") {
                return end("unknown", "Wing output patch not read");
            }
        }
        else if (device.connectionType === "dlive" ||
            device.connectionType === "behringer_wing") {
            return end("unknown", "A mixer has no video");
        }
        return this.head(device, port, media);
    }
    /** Cameras and devices without an adapter: their outputs are heads. */
    head(device, port, media) {
        if (media.kind === "audio" && portCarriesVideo(port)) {
            if (!deviceEmbeddedAudio(device).includes(media.channel)) {
                return end("empty", `${device.name} has no audio on channel ${media.channel}`);
            }
        }
        return end("head", undefined, { label: device.name });
    }
    videohub(device, index, media, path) {
        if (!this.store(device.id))
            return this.noState(device.id);
        const source = this.number(device.id, `/outputs/${index}/source`);
        if (source === undefined) {
            return end("unknown", `Route of ${device.name} output ${index + 1} unknown`);
        }
        const input = this.controlled(device.id, (control) => control.kind === "videohub_input" && control.index === source);
        if (!input) {
            return end("unknown", `${device.name} routes input ${source + 1}, which has no documented port`);
        }
        // Audio follows video: channel N comes from channel N of the input
        return feeds({ node: this.port(input, media, path) });
    }
    atemOutput(device, port, media, path) {
        if (media.kind === "audio") {
            // ATEM audio is not traced: its embedded channels are program audio
            if (!deviceEmbeddedAudio(device).includes(media.channel)) {
                return end("empty", `No audio on channel ${media.channel}`);
            }
            return feeds({
                node: mixNode(device.id, "ATEM program audio", sideOf(media.channel)),
            });
        }
        const control = port.control;
        if (control?.kind === "atem_multiviewer") {
            return end("head", undefined, {
                label: `${device.name} Multiviewer ${control.index + 1}`,
            });
        }
        if (control?.kind !== "atem_aux") {
            return end("unknown", `${portLabel(port)} has no ATEM output address`);
        }
        if (!this.store(device.id))
            return this.noState(device.id);
        const source = this.number(device.id, `/auxiliaries/${control.index}/source`);
        if (source === undefined) {
            return end("unknown", `Source of ${device.name} ${portLabel(port)} unknown`);
        }
        return feeds({ node: this.atemSource(device, source, path) });
    }
    /** An ATEM source: an input port or a point inside the switcher. */
    atemSource(device, source, path) {
        const input = this.controlled(device.id, (control) => control.kind === "atem_input" && control.input_id === source);
        if (input)
            return this.port(input, { kind: "video" }, path);
        const node = {
            deviceID: device.id,
            label: `${device.name} ${atemSourceName(this.inputs.routes ?? {}, device.id, source)}`,
            status: "resolved",
            stateFrom: this.stateFrom(device.id),
            feeds: [],
        };
        if (source >= 1 && source < 1000) {
            return {
                ...node,
                status: "unknown",
                reason: `${device.name} input ${source} has no documented port`,
            };
        }
        const key = `atem:${device.id}:${source}`;
        if (path.has(key))
            return this.cycle(node, node.label);
        const next = new Set(path).add(key);
        const sub = (role, id) => id === undefined
            ? null
            : {
                ...(role ? { role } : {}),
                node: this.atemSource(device, id, next),
            };
        const composite = (list, missing) => {
            const present = list.filter((feed) => !!feed);
            return present.length > 0
                ? { ...node, feeds: present }
                : { ...node, status: "unknown", reason: missing };
        };
        // M/E program (10010, 10020, ...) and preview (10011, ...)
        if (source >= 10010 && source < 11000) {
            const me = Math.floor((source - 10000) / 10) - 1;
            const preview = source % 10 === 1;
            const background = this.number(device.id, `/mixEffects/${me}/${preview ? "previewInput" : "programInput"}`);
            const list = [sub("background", background)];
            if (!preview)
                list.push(...this.atemKeyers(device, me, true, sub));
            return composite(list, `${node.label} unknown`);
        }
        // Clean feeds: the M/E program without the DSKs
        if (source > 7000 && source < 7100) {
            const me = source - 7001;
            const background = this.number(device.id, `/mixEffects/${me}/programInput`);
            return composite([
                sub("background", background),
                ...this.atemKeyers(device, me, false, sub),
            ], `${node.label} unknown`);
        }
        // SuperSource: its art and boxes
        if (source >= 6000 && source < 6100) {
            const ss = source - 6000;
            const prefix = `/superSources/${ss}`;
            const list = [
                sub("art", this.number(device.id, `${prefix}/artFillSource`)),
            ];
            for (let box = 0; box < 8; box++) {
                if (!this.truthy(device.id, `${prefix}/boxes/${box}/enabled`))
                    continue;
                list.push(sub(`box ${box + 1}`, this.number(device.id, `${prefix}/boxes/${box}/source`)));
            }
            return composite(list, `${node.label} unknown`);
        }
        // An aux used as a source: what that aux shows
        if (source > 8000 && source < 8100) {
            const aux = this.number(device.id, `/auxiliaries/${source - 8001}/source`);
            return composite([sub(undefined, aux)], `${node.label} unknown`);
        }
        // Black, colour bars, colour generators, media players, multiviewers
        return { ...node, status: "head" };
    }
    /** Keyers on air on an M/E: upstream keyers (M/E 1), and DSKs on program. */
    atemKeyers(device, me, withDsks, sub) {
        const list = [];
        // The store holds the upstream keyers of M/E 1 only
        if (me === 0) {
            for (let key = 0; key < 4; key++) {
                const prefix = `/upstreamKeyers/${key}`;
                if (!this.truthy(device.id, `${prefix}/onAir`))
                    continue;
                list.push(sub(`key ${key + 1}`, this.number(device.id, `${prefix}/fillSource`)));
            }
        }
        if (withDsks && me === 0) {
            for (let dsk = 0; dsk < 4; dsk++) {
                const prefix = `/downstreamKeyers/${dsk}`;
                if (!this.truthy(device.id, `${prefix}/onAir`))
                    continue;
                list.push(sub(`DSK ${dsk + 1}`, this.number(device.id, `${prefix}/fillSource`)));
            }
        }
        return list;
    }
    /** A DeckLink port used as an output: a vMix output, or lm-io-server. */
    captureCard(card, port, media, path) {
        const output = this.inputs.vmixOutputs?.[port.id];
        if (!output) {
            const lmIo = this.inputs.lmIoServer?.outputs?.find((entry) => entry.port_id === port.id);
            if (lmIo) {
                if (media.kind === "audio" && media.channel > 2) {
                    return end("empty", `No audio on channel ${media.channel}`);
                }
                return end("head", undefined, { label: `lm-io-server "${lmIo.name}"` });
            }
            const address = captureCardPortAddress(port.id, this.wiring.devices, this.wiring.ports);
            return end("unknown", `No vMix output set for ${address?.vmixName ?? portLabel(port)}`);
        }
        const vmix = this.vmixOf(card);
        if (!vmix)
            return end("unknown", "No vMix on this PC");
        const outputLabel = `vMix ${VMIX_OUTPUT_LABELS[output]}`;
        if (media.kind === "audio") {
            if (media.channel > 2) {
                return end("empty", `No audio on channel ${media.channel}`);
            }
            // vMix audio is not traced: the output carries one of its buses
            const bus = this.text(vmix.id, `/outputs/${output}/audioBus`) ?? "Master";
            return feeds({
                node: mixNode(vmix.id, `vMix ${bus}`, sideOf(media.channel)),
            });
        }
        if (!this.store(vmix.id))
            return this.noState(vmix.id);
        const source = this.text(vmix.id, `/outputs/${output}/source`) ??
            // vMix's main output shows program unless set otherwise
            (output === "output1" ? "Program" : undefined);
        const point = (label) => ({
            deviceID: vmix.id,
            label,
            status: "resolved",
            stateFrom: this.stateFrom(vmix.id),
            feeds: [],
        });
        if (!source) {
            return end("unknown", `vMix doesn't report what ${outputLabel} shows`);
        }
        if (source === "Program" || source === "Preview") {
            const preview = source === "Preview";
            const node = point(`vMix ${source}`);
            const main = this.number(vmix.id, preview ? "/preview" : "/active");
            node.feeds = main
                ? [{ role: "background", node: this.vmixInput(vmix, main) }]
                : [];
            for (let overlay = 1; overlay <= 4; overlay++) {
                const input = this.number(vmix.id, `/overlays/${overlay}/${preview ? "preview" : "input"}`);
                if (input) {
                    node.feeds.push({
                        role: `overlay ${overlay}`,
                        node: this.vmixInput(vmix, input),
                    });
                }
            }
            if (node.feeds.length === 0) {
                return feeds({
                    node: { ...node, status: "unknown", reason: `${node.label} unknown` },
                });
            }
            return feeds({ node });
        }
        if (source === "Mix") {
            const mix = this.number(vmix.id, `/outputs/${output}/mix`);
            const active = mix
                ? this.number(vmix.id, `/mixes/${mix}/active`)
                : undefined;
            const node = point(`vMix Mix ${mix ?? "?"}`);
            return feeds({
                node: active
                    ? { ...node, feeds: [{ node: this.vmixInput(vmix, active) }] }
                    : { ...node, status: "unknown", reason: `${node.label} unknown` },
            });
        }
        const input = this.number(vmix.id, `/outputs/${output}/input`);
        if (input)
            return feeds({ node: this.vmixInput(vmix, input) });
        return end("unknown", `vMix ${source} on ${outputLabel} not understood`);
    }
    vmixInput(vmix, input) {
        const title = this.text(vmix.id, `/inputs/${input}/title`);
        return {
            deviceID: vmix.id,
            label: title ? `vMix "${title}"` : `vMix input ${input}`,
            status: "head",
            stateFrom: this.stateFrom(vmix.id),
            feeds: [],
        };
    }
    // ── Dante ──
    danteOutputPort(device, port, media, path) {
        const channels = this.dantePorts(device).get(port.id);
        if (!channels || channels.direction !== "rx")
            return null;
        if (media.channel > channels.count)
            return null;
        return feeds({
            node: this.danteRx(device.danteName.trim(), channels.first + media.channel - 1, path),
        });
    }
    /** An rx channel: fed by its subscribed tx channel. */
    danteRx(name, index, path) {
        const dante = this.inputs.dante;
        const device = this.deviceByDanteName.get(name);
        const config = dante?.config.devices.find((d) => d.name === name);
        const rx = config?.rx.find((channel) => channel.index === index);
        const node = {
            deviceID: device?.id ?? "",
            danteChannel: { device: name, index, direction: "rx" },
            label: `${name} rx ${index}${rx ? ` "${danteChannelName(rx)}"` : ""}`,
            status: "resolved",
            stateFrom: dante?.from,
            feeds: [],
        };
        if (!dante)
            return { ...node, status: "unknown", reason: "Dante state unknown" };
        if (!config) {
            return {
                ...node,
                status: "unknown",
                reason: `${name} is not on the Dante network`,
            };
        }
        if (!rx) {
            return {
                ...node,
                status: "unknown",
                reason: `${name} has no rx channel ${index}`,
            };
        }
        const key = `dante-rx:${name}:${index}`;
        if (path.has(key))
            return this.cycle(node, node.label);
        const next = new Set(path).add(key);
        if (!rx.source)
            return { ...node, status: "empty", reason: "No subscription" };
        const source = rx.source;
        if ("unresolved_name" in source) {
            return {
                ...node,
                status: "unknown",
                reason: `Subscribed to "${source.unresolved_name}@${source.device}", which doesn't exist`,
            };
        }
        const status = dante.routes?.[danteChannelKey(name, index)];
        if (status && status.state !== "connected" && status.state !== "pending") {
            return {
                ...node,
                status: "unknown",
                reason: status.state === "device_offline"
                    ? "device offline"
                    : status.state === "unresolved"
                        ? "subscription unresolved"
                        : "subscription error",
            };
        }
        node.feeds = [{ node: this.danteTx(source.device, source.index, next) }];
        return node;
    }
    /** A tx channel: answered by the adapter of its device. */
    danteTx(name, index, path) {
        const dante = this.inputs.dante;
        const device = this.deviceByDanteName.get(name);
        const tx = dante?.config.devices
            .find((d) => d.name === name)
            ?.tx.find((channel) => channel.index === index);
        const txName = tx ? danteChannelName(tx) : String(index);
        const node = {
            deviceID: device?.id ?? "",
            danteChannel: { device: name, index, direction: "tx" },
            // tx names ("Main L") label the hop, but never decide anything
            label: `${name} tx ${index}${tx ? ` "${txName}"` : ""}`,
            status: "resolved",
            stateFrom: dante?.from,
            feeds: [],
        };
        if (!device) {
            return {
                ...node,
                status: "unknown",
                reason: `No studio device has the Dante name ${name}`,
            };
        }
        if (dante?.devices?.[name] && !dante.devices[name].online) {
            return { ...node, status: "unknown", reason: "device offline" };
        }
        const key = `dante-tx:${name}:${index}`;
        if (path.has(key))
            return this.cycle(node, node.label);
        const next = new Set(path).add(key);
        if (this.isDanteOnPorts(device)) {
            for (const [portID, channels] of this.dantePorts(device)) {
                if (channels.direction !== "tx")
                    continue;
                if (index < channels.first ||
                    index >= channels.first + channels.count) {
                    continue;
                }
                const port = this.portByID.get(portID);
                const input = this.port(port, { kind: "audio", channel: index - channels.first + 1 }, next, true);
                if (portCarriesVideo(port))
                    input.embedding = "de-embedded";
                node.feeds = [{ node: input }];
                return node;
            }
        }
        if (device.connectionType === "dlive") {
            return applyResult({ ...node, stateFrom: this.stateFrom(device.id) }, this.dlive(device, "dante", index));
        }
        if (device.connectionType === "behringer_wing") {
            return {
                ...node,
                status: "unknown",
                reason: "Wing output patch not read",
            };
        }
        if (this.hasVmix(device)) {
            // vMix audio is not traced: the DVS channels carry its buses
            node.feeds = [{ node: mixNode(device.id, "vMix audio") }];
            return node;
        }
        // A Dante-native source (a mic receiver, a PC)
        return { ...node, label: `${device.name} "${txName}"`, status: "head" };
    }
    // ── Mixers ──
    /**
     * A dLive output channel: fed by what its output patch puts there, read
     * from the store at `/outputPatch/<socket>/<channel>/{kind,name,side}`
     * (`socket`: "dante", or "port<N>" for a port numbered N). Until the
     * output patch is read the lane ends unknown.
     */
    dlive(device, socket, channel) {
        const prefix = `/outputPatch/${socket}/${channel}`;
        const kind = this.text(device.id, `${prefix}/kind`);
        if (!kind)
            return end("unknown", "dLive output patch not read");
        const name = this.text(device.id, `${prefix}/name`) ??
            `${device.name} ${socket} ${channel}`;
        const side = this.text(device.id, `${prefix}/side`);
        if (kind === "empty")
            return end("empty", "Nothing patched");
        return feeds({
            node: {
                deviceID: device.id,
                label: name,
                status: kind === "mix" ? "mix" : "head",
                ...(side === "L" || side === "R" ? { side } : {}),
                stateFrom: this.stateFrom(device.id),
                feeds: [],
            },
        });
    }
}
function linkInfo(link) {
    return {
        id: link.id,
        configID: link.configID ?? null,
        patchStatus: link.patchStatus ?? null,
    };
}
function mixNode(deviceID, label, side) {
    return {
        deviceID,
        label,
        status: "mix",
        ...(side ? { side } : {}),
        feeds: [],
    };
}
function applyResult(node, result) {
    if (result.kind === "feeds")
        return { ...node, feeds: result.feeds };
    return {
        ...node,
        status: result.status,
        ...(result.label ? { label: result.label } : {}),
        ...(result.reason ? { reason: result.reason } : {}),
        ...(result.side ? { side: result.side } : {}),
        feeds: [],
    };
}
// ─── Entry points ─────────────────────────────────────────────
/**
 * Everything on `portID`, traced back to its heads: one tree for the video
 * (null for audio-only ports) and one per audio channel of the port. One
 * device failing never fails the trace: its node ends `unknown` with a
 * reason. Cycles are detected per path, so the same camera may be both the
 * background and a SuperSource box.
 */
export function traceSignal(portID, wiring, inputs = {}) {
    const tracer = new Tracer(wiring, inputs);
    const port = tracer.portByID.get(portID);
    if (!port)
        return null;
    const video = portCarriesVideo(port)
        ? tracer.port(port, { kind: "video" }, new Set())
        : null;
    const audio = Array.from({ length: portAudioChannels(port) }, (_, i) => tracer.port(port, { kind: "audio", channel: i + 1 }, new Set()));
    return { portID, video, audio };
}
/** Every node of a tree, depth first. */
export function* traceNodes(node) {
    yield node;
    for (const feed of node.feeds)
        yield* traceNodes(feed.node);
}
/** The nodes without feeds: heads, mixes and dead ends. */
export function traceLeaves(node) {
    return [...traceNodes(node)].filter((n) => n.feeds.length === 0);
}
